// Bridge slide — Phase 5 React port of kula-create's compositor bridge.
//
// Sits between two scenario slides in a scenario group. Renders a continuity
// beat: a response-aware callout (named via summariseResponse on the prior
// slide's payload) layered over the designer-authored narrative text.
//
// Not data-capturing — no onActivityResponse, no interaction registry entry.
// Just the "Continue" button advances the learner to the next slide via the
// lesson viewer's own nav (we don't manage navigation here).

import { useMemo } from 'react';
import type { BridgeSlideData, Theme } from '../../slideTypes';
import { slideTypographyStyle } from '../../slideTypes';
import { summariseResponse } from '../../../../../lib/scenario-builder/compositor/responseRecap';

interface Props {
  data: BridgeSlideData;
  theme: Theme;
  isEditing: boolean;
  onUpdate: (data: BridgeSlideData) => void;
  /**
   * Optional context about the slide that came before this bridge. Provided
   * by the lesson viewer when both slides share a scenarioGroupId. The
   * BridgeSlide uses it to render a response-aware callout above the
   * designer-authored narrative. When absent (e.g. a bridge being previewed
   * standalone, or the prior slide hasn't been responded to in-session), the
   * bridge silently falls back to designer narrative + a generic continue.
   */
  priorSlideContext?: {
    templateId: string;
    response: Record<string, unknown> | null | undefined;
  };
}

export function BridgeSlide({ data, theme, isEditing, onUpdate, priorSlideContext }: Props) {
  if (isEditing) {
    return <BridgeEditor data={data} theme={theme} onUpdate={onUpdate} />;
  }
  return <BridgeStudent data={data} theme={theme} priorSlideContext={priorSlideContext} />;
}

// ---------------------------------------------------------------------------
// Student-facing renderer
// ---------------------------------------------------------------------------

function BridgeStudent({
  data,
  theme,
  priorSlideContext,
}: {
  data: BridgeSlideData;
  theme: Theme;
  priorSlideContext?: Props['priorSlideContext'];
}) {
  const calloutText = useMemo(() => {
    if (!data.showResponseRecap) return null;
    if (!priorSlideContext) return null;
    return summariseResponse(priorSlideContext.templateId, priorSlideContext.response);
  }, [data.showResponseRecap, priorSlideContext]);

  const hasContent = Boolean(calloutText || data.narrativeText);

  return (
    <div
      className="w-full h-full flex items-center justify-center px-6"
      style={{
        ...slideTypographyStyle(theme, 'body'),
        backgroundColor: theme.colors.background,
        color: theme.colors.text,
      }}
      data-testid="bridge-slide"
    >
      <div className="max-w-2xl w-full mx-auto py-12 text-center">
        {calloutText && (
          <div
            className="text-left rounded-r-xl px-6 py-5 mb-4"
            style={{
              backgroundColor: `color-mix(in srgb, ${theme.colors.accent} 8%, ${theme.colors.surface})`,
              borderLeft: `3px solid ${theme.colors.accent}`,
              color: theme.colors.text,
              lineHeight: 1.7,
            }}
            data-testid="bridge-callout"
          >
            {calloutText}
          </div>
        )}

        {data.narrativeText && (
          <p
            className="italic mb-8"
            style={{
              color: theme.colors.textMuted,
              fontSize: '0.95rem',
              lineHeight: 1.6,
            }}
            data-testid="bridge-narrative"
          >
            {data.narrativeText}
          </p>
        )}

        {!hasContent && (
          <p
            className="italic mb-8"
            style={{ color: theme.colors.textMuted }}
            data-testid="bridge-fallback"
          >
            Building on what you just explored, the next section takes this further.
          </p>
        )}

        {/* The "continue" affordance is the lesson viewer's nav — nothing here. */}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Editor — minimal. Bridge slides are AI-authored; structural edits are
// regeneration-only. We expose narrativeText (free-text) + the recap toggle.
// ---------------------------------------------------------------------------

function BridgeEditor({
  data,
  theme,
  onUpdate,
}: {
  data: BridgeSlideData;
  theme: Theme;
  onUpdate: (data: BridgeSlideData) => void;
}) {
  return (
    <div
      className="w-full h-full overflow-y-auto px-6 py-8"
      style={{
        ...slideTypographyStyle(theme, 'body'),
        backgroundColor: theme.colors.background,
        color: theme.colors.text,
      }}
    >
      <div className="max-w-2xl mx-auto space-y-5">
        <div className="text-xs uppercase tracking-wider opacity-60">Bridge slide</div>

        <label className="block">
          <div className="text-sm font-medium mb-2">Narrative text</div>
          <textarea
            className="w-full p-3 border rounded-md font-normal"
            rows={4}
            value={data.narrativeText}
            onChange={(e) => onUpdate({ ...data, narrativeText: e.target.value })}
            placeholder="The continuity beat between sections — what shifts now?"
            style={{
              backgroundColor: theme.colors.surface,
              borderColor: theme.colors.textMuted,
              color: theme.colors.text,
            }}
          />
        </label>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={data.showResponseRecap}
            onChange={(e) => onUpdate({ ...data, showResponseRecap: e.target.checked })}
          />
          Show response-aware callout (recaps what the learner just did on the prior slide)
        </label>

        <p className="text-xs italic opacity-60 mt-6">
          Bridges are auto-inserted between scenario slides in a group. Structural changes —
          which slides they sit between, the prior-slide binding — are AI-managed; regenerate
          the scenario group to restructure.
        </p>
      </div>
    </div>
  );
}
